import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const setPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters."),
    confirmPassword: z.string().min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

interface SetPasswordProps {
  email: string;
  companyName: string | null;
  tokenType: "invite" | "password_reset" | null;
  onSubmit: (password: string) => void;
  loading: boolean;
  error: string | null;
}

export default function SetPassword({
  email,
  companyName,
  tokenType,
  onSubmit,
  loading,
  error,
}: SetPasswordProps) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
  });

  const onFormSubmit = (data: SetPasswordFormValues) => {
    onSubmit(data.password);
  };

  const title =
    tokenType === "password_reset"
      ? "Reset your password"
      : "Set your password";

  const subtitle =
    tokenType === "password_reset"
      ? "Enter a new password for your account."
      : "Create a password to complete your account setup.";

  return (
    <div>
      <h1 className="h3 fw-bold text-dark mb-1">{title}</h1>
      <p className="text-muted mb-1">{subtitle}</p>
      <p className="text-muted mb-4">
        <strong>{email}</strong>
        {companyName && <> &middot; {companyName}</>}
      </p>

      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <div className="mb-3">
          <label htmlFor="password" className="form-label fw-medium">
            New password
          </label>
          <div className="input-group">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              autoFocus
              className={`form-control form-control-lg${
                errors.password ? " is-invalid" : ""
              }`}
              placeholder="At least 8 characters"
              disabled={loading}
              {...register("password")}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
            {errors.password && (
              <div className="invalid-feedback">{errors.password.message}</div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="confirmPassword" className="form-label fw-medium">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            className={`form-control form-control-lg${
              errors.confirmPassword ? " is-invalid" : ""
            }`}
            placeholder="Re-enter your password"
            disabled={loading}
            {...register("confirmPassword")}
          />
          {errors.confirmPassword && (
            <div className="invalid-feedback">
              {errors.confirmPassword.message}
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-lg w-100"
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Setting password...
            </>
          ) : (
            "Set Password"
          )}
        </button>
      </form>
    </div>
  );
}
